// One player's side of a game's video call.
//
// Everyone in the call is connected to everyone else directly (at most five
// people, so at most four connections each). The server only decides who is
// in the call and passes connection details between two browsers; if two
// players cannot reach each other directly, the TURN server it names relays
// the (still encrypted) packets.
//
// Who calls whom is fixed, so two browsers never both try to start the same
// connection: whoever joins calls everyone already there, and only that side
// ever sends offers — including the ones that restart a failed connection.
//
// Audio and video each get one transceiver per connection for its whole life.
// Turning the camera off or on swaps the track on it (replaceTrack), so it
// never needs renegotiating and nobody ends up seeing only one side.

const MEDIA_VIDEO = {
    width: { ideal: 640 },
    height: { ideal: 360 },
    frameRate: { ideal: 24, max: 24 },
    facingMode: 'user'
};
const MEDIA_AUDIO = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

// Five people means everyone uploads four copies of their video.
const BITRATE_SMALL_CALL = 800000;
const BITRATE_FULL_CALL = 350000;
const FULL_CALL_PEERS = 3;

// How long a connection may take before its tile says it couldn't connect.
const CONNECT_TIMEOUT_MS = 15000;
// A blip on the network often recovers on its own; give it this long first.
const DISCONNECT_GRACE_MS = 5000;

const AUTO_JOIN_KEY = 'call:autoJoin';

export const autoJoinTarget = () => {
    try {
        return sessionStorage.getItem(AUTO_JOIN_KEY);
    } catch (error) {
        return null;
    }
};
export const setAutoJoinTarget = (gameId) => {
    try {
        if (gameId) {
            sessionStorage.setItem(AUTO_JOIN_KEY, gameId);
        } else {
            sessionStorage.removeItem(AUTO_JOIN_KEY);
        }
    } catch (error) { /* private mode */ }
};

// Mute and camera-off are remembered for the tab, so a reload or a dropped
// connection never brings someone back with a live microphone they had
// turned off.
const MEDIA_KEY = 'call:media';
const savedMedia = () => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(MEDIA_KEY) || '{}');
        return { audio: saved.audio !== false, video: saved.video !== false };
    } catch (error) {
        return { audio: true, video: true };
    }
};
const saveMedia = (media) => {
    try {
        sessionStorage.setItem(MEDIA_KEY, JSON.stringify(media));
    } catch (error) { /* private mode */ }
};

// Forces every connection through the TURN relay. For checking the relay
// works: localStorage.setItem('call:relayOnly', '1').
const relayOnly = () => {
    try {
        return localStorage.getItem('call:relayOnly') === '1';
    } catch (error) {
        return false;
    }
};

const describeMediaError = (error) => {
    switch (error?.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'Camera and microphone are blocked for this site. You can still see and hear the others.';
        case 'NotFoundError':
        case 'OverconstrainedError':
            return 'No camera or microphone was found. You can still see and hear the others.';
        case 'NotReadableError':
            return 'Your camera or microphone is being used by another app.';
        default:
            return 'Could not start your camera or microphone.';
    }
};

export default class CallSession {
    constructor({ gameId, socket, command, onChange, onEvent }) {
        this.gameId = gameId;
        this.socket = socket;
        this.command = command;
        this.onChange = onChange;
        this.onEvent = onEvent || (() => {});

        this.status = 'idle'; // idle | joining | in-call | reconnecting
        // True while the browser is asking for camera and microphone access.
        this.askingPermission = false;
        this.error = null;
        this.notice = null;
        this.sessionId = null;
        this.iceServers = [];
        this.joinedAt = null;

        this.localStream = null;
        this.audioTrack = null;
        this.videoTrack = null;
        const media = savedMedia();
        this.audioOn = media.audio;
        this.videoOn = media.video;

        // userId -> peer (see createPeer)
        this.peers = new Map();
        this.participants = [];
        this.destroyed = false;

        this.onSignal = this.onSignal.bind(this);
        this.onEnded = this.onEnded.bind(this);
        this.onDisconnect = this.onDisconnect.bind(this);
        this.onDeclined = this.onDeclined.bind(this);
        socket.on('call:signal', this.onSignal);
        socket.on('call:ended', this.onEnded);
        socket.on('disconnect', this.onDisconnect);
        socket.on('call:declined', this.onDeclined);
    }

    // ------------------------------------------------------------------ state

    changed() {
        if (!this.destroyed) {
            this.onChange(this.snapshot());
        }
    }

    snapshot() {
        return {
            status: this.status,
            askingPermission: this.askingPermission,
            error: this.error,
            notice: this.notice,
            localStream: this.localStream,
            hasAudio: Boolean(this.audioTrack),
            hasVideo: Boolean(this.videoTrack),
            audioOn: this.audioOn && Boolean(this.audioTrack),
            videoOn: this.videoOn && Boolean(this.videoTrack),
            peers: [...this.peers.values()].map(peer => ({
                userId: peer.userId,
                stream: peer.stream,
                state: peer.state
            }))
        };
    }

    // ------------------------------------------------------------------ media

    async startMedia() {
        if (this.localStream) {
            return;
        }
        this.notice = null;
        this.askingPermission = true;
        this.changed();
        let stream = null;
        try {
            // With the camera left off, do not even switch it on.
            stream = await navigator.mediaDevices.getUserMedia({ audio: MEDIA_AUDIO, video: this.videoOn ? MEDIA_VIDEO : false });
        } catch (error) {
            try {
                // No camera (or it is busy): carry on with just the microphone.
                stream = await navigator.mediaDevices.getUserMedia({ audio: MEDIA_AUDIO });
                this.notice = describeMediaError(error).replace(' You can still see and hear the others.', ' Joining with your microphone only.');
            } catch (audioError) {
                // Nothing at all: join to watch and listen.
                stream = new MediaStream();
                this.notice = describeMediaError(audioError);
            }
        }
        this.askingPermission = false;
        this.localStream = stream;
        this.audioTrack = stream.getAudioTracks()[0] || null;
        this.videoTrack = stream.getVideoTracks()[0] || null;
        if (this.audioTrack) {
            this.audioTrack.enabled = this.audioOn;
        }
        if (this.videoTrack && !this.videoOn) {
            this.dropVideoTrack();
        }
    }

    stopMedia() {
        (this.localStream?.getTracks() || []).forEach(track => track.stop());
        this.localStream = null;
        this.audioTrack = null;
        this.videoTrack = null;
    }

    dropVideoTrack() {
        if (!this.videoTrack) {
            return;
        }
        this.videoTrack.stop();
        this.localStream?.removeTrack(this.videoTrack);
        this.videoTrack = null;
    }

    mediaState() {
        return { audio: this.audioOn && Boolean(this.audioTrack), video: this.videoOn && Boolean(this.videoTrack) };
    }

    shareMediaState() {
        this.command('call:media', this.mediaState()).catch(() => { /* cosmetic */ });
    }

    async toggleAudio() {
        if (!this.audioTrack) {
            return;
        }
        this.audioOn = !this.audioOn;
        this.audioTrack.enabled = this.audioOn;
        saveMedia({ audio: this.audioOn, video: this.videoOn });
        this.changed();
        this.shareMediaState();
    }

    async toggleVideo() {
        if (this.videoOn && this.videoTrack) {
            // Off: stop the camera for real, so its light goes out.
            this.videoOn = false;
            this.dropVideoTrack();
            this.peers.forEach(peer => this.sendTrack(peer, 'video', null));
        } else {
            this.videoOn = true;
            try {
                const camera = await navigator.mediaDevices.getUserMedia({ video: MEDIA_VIDEO });
                this.videoTrack = camera.getVideoTracks()[0];
                if (!this.localStream) {
                    this.localStream = new MediaStream();
                }
                this.localStream.addTrack(this.videoTrack);
                this.peers.forEach(peer => this.sendTrack(peer, 'video', this.videoTrack));
                this.notice = null;
            } catch (error) {
                this.videoOn = false;
                this.notice = describeMediaError(error);
            }
        }
        saveMedia({ audio: this.audioOn, video: this.videoOn });
        // A new MediaStream object makes the self view pick up the change.
        if (this.localStream) {
            this.localStream = new MediaStream(this.localStream.getTracks());
        }
        this.changed();
        this.shareMediaState();
    }

    // ------------------------------------------------------------- joining

    async join() {
        if (this.status === 'joining' || this.status === 'in-call') {
            return;
        }
        const rejoining = this.status === 'reconnecting';
        this.status = 'joining';
        this.error = null;
        this.changed();

        try {
            await this.startMedia();
            if (this.destroyed) {
                this.stopMedia();
                return;
            }
            const answer = await this.command('call:join', { gameId: this.gameId, ...this.mediaState() });
            if (this.destroyed) {
                this.command('call:leave', {}).catch(() => {});
                this.stopMedia();
                return;
            }
            this.sessionId = answer.sessionId;
            this.iceServers = answer.iceServers || [];
            this.status = 'in-call';
            if (!rejoining) {
                this.joinedAt = Date.now();
            }
            setAutoJoinTarget(this.gameId);
            // Whoever joins calls everyone already in.
            (answer.participants || []).forEach(p => this.createPeer(p.userId, p.sessionId, true));
            this.onEvent(rejoining ? 'call_rejoined' : 'call_joined', { others: (answer.participants || []).length });
            this.changed();
        } catch (error) {
            // Let go of the camera rather than leave its light on for a call
            // we are not in. Joining again (by hand, or automatically on the
            // next reconnect) starts it afresh.
            this.stopMedia();
            this.status = 'idle';
            this.error = error.message || 'Could not join the call';
            this.changed();
        }
    }

    /**
     * Hangs up. `rejoinLater` keeps this tab marked as on the call — for when
     * the call is only out of reach for a moment (the window was made too
     * narrow for the board) rather than left on purpose.
     */
    leave({ rejoinLater = false } = {}) {
        if (this.status !== 'idle') {
            this.command('call:leave', {}).catch(() => { /* the server drops us on disconnect anyway */ });
            this.onEvent('call_left', { seconds: this.joinedAt ? Math.round((Date.now() - this.joinedAt) / 1000) : 0 });
        }
        if (!rejoinLater) {
            setAutoJoinTarget(null);
        }
        this.teardown();
        this.changed();
    }

    teardown() {
        this.peers.forEach(peer => this.closePeer(peer));
        this.peers.clear();
        this.stopMedia();
        this.status = 'idle';
        this.sessionId = null;
        this.joinedAt = null;
    }

    /** The component is going away (another game, another page). */
    destroy() {
        if (this.status === 'in-call' || this.status === 'joining') {
            this.command('call:leave', {}).catch(() => {});
        }
        this.teardown();
        this.destroyed = true;
        this.socket.off('call:signal', this.onSignal);
        this.socket.off('call:ended', this.onEnded);
        this.socket.off('disconnect', this.onDisconnect);
        this.socket.off('call:declined', this.onDeclined);
    }

    /**
     * A rematch began and the server moved the call to the new game. Every
     * connection stays as it is; only the game it belongs to changes.
     */
    carryTo(gameId) {
        // The old game can be pushed again before the page has moved on;
        // carrying twice must not forget where the call came from.
        if (this.gameId === String(gameId)) {
            return;
        }
        this.carriedFrom = this.gameId;
        this.gameId = String(gameId);
        if (this.status === 'in-call' || this.status === 'reconnecting') {
            setAutoJoinTarget(this.gameId);
        }
    }

    /** Turns down the ring; whoever started the call is told. */
    decline() {
        this.command('call:decline', { gameId: this.gameId }).catch(() => {});
    }

    onDeclined({ gameId, name }) {
        if (String(gameId) !== String(this.gameId) || this.status !== 'in-call') {
            return;
        }
        const notice = `${name} can't join right now.`;
        this.notice = notice;
        this.changed();
        setTimeout(() => {
            if (this.notice === notice) {
                this.notice = null;
                this.changed();
            }
        }, 8000);
    }

    onEnded({ gameId, reason }) {
        if (String(gameId) !== String(this.gameId)) {
            return;
        }
        this.teardown();
        setAutoJoinTarget(null);
        this.notice = reason === 'replaced' ? 'You joined this call from another tab.' : 'The call ended.';
        this.changed();
    }

    // Losing the connection to the game server takes us out of the call on
    // its side, and the others hang up on us. Keep the camera running and
    // join again as soon as we are back.
    onDisconnect() {
        if (this.status !== 'in-call' && this.status !== 'joining') {
            return;
        }
        this.peers.forEach(peer => this.closePeer(peer));
        this.peers.clear();
        this.status = 'reconnecting';
        this.changed();
    }

    /**
     * Who the server says is in the call. Anyone who left — or rejoined, with
     * a new session — loses the connection we had to them.
     */
    syncParticipants(participants) {
        this.participants = participants || [];
        if (this.status !== 'in-call') {
            return;
        }
        const current = new Map(this.participants.map(p => [p.userId, p.sessionId]));
        this.peers.forEach(peer => {
            if (current.get(peer.userId) !== peer.sessionId) {
                this.closePeer(peer);
                this.peers.delete(peer.userId);
            }
        });
        this.applyBitrate();
        this.changed();
    }

    // ---------------------------------------------------------- connections

    createPeer(userId, sessionId, initiator) {
        const pc = new RTCPeerConnection({
            iceServers: this.iceServers,
            iceTransportPolicy: relayOnly() ? 'relay' : 'all'
        });
        const peer = {
            userId,
            sessionId,
            initiator,
            pc,
            stream: new MediaStream(),
            state: 'connecting',
            pendingCandidates: [],
            makingOffer: false,
            reported: false,
            connectTimer: null,
            graceTimer: null
        };
        this.peers.set(userId, peer);

        if (initiator) {
            pc.addTransceiver(this.audioTrack || 'audio', { direction: 'sendrecv', streams: [this.localStream] });
            pc.addTransceiver(this.videoTrack || 'video', { direction: 'sendrecv', streams: [this.localStream] });
        }

        pc.ontrack = (event) => {
            if (!peer.stream.getTracks().includes(event.track)) {
                peer.stream.addTrack(event.track);
                // A new object, so React re-renders the tile with it.
                peer.stream = new MediaStream(peer.stream.getTracks());
                this.changed();
            }
        };
        pc.onicecandidate = ({ candidate }) => {
            if (candidate) {
                this.signal(userId, { candidate: candidate.toJSON() });
            }
        };
        pc.onnegotiationneeded = async () => {
            // Only the side that placed the call ever offers.
            if (!peer.initiator || peer.makingOffer) {
                return;
            }
            try {
                peer.makingOffer = true;
                await pc.setLocalDescription(await pc.createOffer());
                this.signal(userId, { description: pc.localDescription.toJSON() });
            } catch (error) {
                console.warn('[call] offer failed', error);
            } finally {
                peer.makingOffer = false;
            }
        };
        pc.onconnectionstatechange = () => this.onConnectionState(peer);

        peer.connectTimer = setTimeout(() => {
            if (peer.state !== 'connected') {
                this.setPeerState(peer, 'failed');
                this.report(peer, false);
            }
        }, CONNECT_TIMEOUT_MS);

        this.applyBitrate();
        this.changed();
        return peer;
    }

    closePeer(peer) {
        clearTimeout(peer.connectTimer);
        clearTimeout(peer.graceTimer);
        peer.pc.onicecandidate = null;
        peer.pc.ontrack = null;
        peer.pc.onnegotiationneeded = null;
        peer.pc.onconnectionstatechange = null;
        peer.pc.close();
    }

    setPeerState(peer, state) {
        if (peer.state !== state) {
            peer.state = state;
            this.changed();
        }
    }

    onConnectionState(peer) {
        const state = peer.pc.connectionState;
        if (state === 'connected') {
            clearTimeout(peer.connectTimer);
            clearTimeout(peer.graceTimer);
            this.setPeerState(peer, 'connected');
            this.applyBitrate();
            this.report(peer, true);
        } else if (state === 'disconnected') {
            this.setPeerState(peer, 'reconnecting');
            clearTimeout(peer.graceTimer);
            peer.graceTimer = setTimeout(() => {
                if (peer.pc.connectionState !== 'connected') {
                    this.restart(peer);
                }
            }, DISCONNECT_GRACE_MS);
        } else if (state === 'failed') {
            this.setPeerState(peer, 'reconnecting');
            this.restart(peer);
        }
    }

    // A connection that dropped is restarted by the side that placed the call;
    // the other side answers the new offer as usual.
    restart(peer) {
        if (!peer.initiator || !this.peers.has(peer.userId)) {
            return;
        }
        clearTimeout(peer.connectTimer);
        peer.connectTimer = setTimeout(() => {
            if (peer.state !== 'connected') {
                this.setPeerState(peer, 'failed');
            }
        }, CONNECT_TIMEOUT_MS);
        peer.pc.restartIce();
    }

    /** Analytics: whether each connection worked, and whether it needed the relay. */
    async report(peer, connected) {
        if (peer.reported) {
            return;
        }
        peer.reported = true;
        if (!connected) {
            this.onEvent('call_peer_failed', {});
            return;
        }
        let relay = false;
        try {
            const stats = await peer.pc.getStats();
            let pair = null;
            stats.forEach(report => {
                if (report.type === 'transport' && report.selectedCandidatePairId) {
                    pair = stats.get(report.selectedCandidatePairId);
                }
            });
            const local = pair && stats.get(pair.localCandidateId);
            relay = local?.candidateType === 'relay';
        } catch (error) { /* stats are best effort */ }
        this.onEvent('call_peer_connected', { relay });
    }

    applyBitrate() {
        const maxBitrate = this.peers.size >= FULL_CALL_PEERS ? BITRATE_FULL_CALL : BITRATE_SMALL_CALL;
        this.peers.forEach(peer => {
            const sender = this.transceiver(peer, 'video')?.sender;
            if (!sender) {
                return;
            }
            const params = sender.getParameters();
            if (!params.encodings || params.encodings.length === 0) {
                // Not negotiated yet; this runs again once connected.
                return;
            }
            if (params.encodings[0].maxBitrate === maxBitrate) {
                return;
            }
            params.encodings[0].maxBitrate = maxBitrate;
            sender.setParameters(params).catch(() => { /* not supported everywhere */ });
        });
    }

    transceiver(peer, kind) {
        return peer.pc.getTransceivers().find(t => t.receiver.track?.kind === kind) || null;
    }

    sendTrack(peer, kind, track) {
        const transceiver = this.transceiver(peer, kind);
        if (transceiver) {
            transceiver.sender.replaceTrack(track).catch(error => console.warn('[call] replaceTrack failed', error));
        }
    }

    signal(to, payload) {
        this.command('call:signal', { to, ...payload }).catch(() => {
            // They have left; their departure arrives as a participants update.
        });
    }

    async onSignal({ from, sessionId, description, candidate }) {
        if (this.status !== 'in-call') {
            return;
        }
        let peer = this.peers.get(from);

        if (!peer || peer.sessionId !== sessionId) {
            // Only an offer can start a connection: someone new is calling,
            // or someone came back with a new session.
            if (description?.type !== 'offer') {
                return;
            }
            if (peer) {
                this.closePeer(peer);
                this.peers.delete(from);
            }
            peer = this.createPeer(from, sessionId, false);
        }
        const { pc } = peer;

        try {
            if (description) {
                if (description.type === 'offer') {
                    await pc.setRemoteDescription(description);
                    // Send our audio and video back on the transceivers the
                    // offer created.
                    for (const transceiver of pc.getTransceivers()) {
                        const kind = transceiver.receiver.track?.kind;
                        const track = kind === 'audio' ? this.audioTrack : kind === 'video' ? this.videoTrack : null;
                        if (transceiver.sender.track !== track) {
                            await transceiver.sender.replaceTrack(track);
                        }
                        if (this.localStream && transceiver.sender.setStreams) {
                            transceiver.sender.setStreams(this.localStream);
                        }
                        transceiver.direction = 'sendrecv';
                    }
                    await pc.setLocalDescription(await pc.createAnswer());
                    this.signal(from, { description: pc.localDescription.toJSON() });
                } else if (description.type === 'answer' && pc.signalingState === 'have-local-offer') {
                    await pc.setRemoteDescription(description);
                }
                const pending = peer.pendingCandidates;
                peer.pendingCandidates = [];
                for (const queued of pending) {
                    await pc.addIceCandidate(queued).catch(() => {});
                }
            } else if (candidate) {
                if (pc.remoteDescription) {
                    await pc.addIceCandidate(candidate).catch(() => {});
                } else {
                    peer.pendingCandidates.push(candidate);
                }
            }
        } catch (error) {
            console.warn('[call] signal failed', error);
        }
    }
}
